import React, { FC } from 'react';

import {
	EvContactSupportEmailLink,
	EVINCED_SUPPORT_EMAIL,
	TITLE_MODES
} from '@evinced-private/ui-common';

import { getErrorMessage, isClientError } from 'src/common/helpers/ApiErrorHelper';
import { IServerError } from 'src/common/interfaces/ServerError';
import { Logger } from 'src/common/services/Logger';

import { PlatformPopup } from '../platform-popup/PlatformPopup';

import './AlertPopup.scss';

export interface IAlertPopup {
	/**
	 * Error message to display
	 */
	errorMessage?: string;

	/**
	 * popup's title
	 */
	title?: string;

	/**
	 * Server error object
	 */
	serverError?: IServerError;

	/** popup is shown only if isOpened==true */
	isOpened?: boolean;

	/**
	 * A callback function to be called when user hits OK to close the popup.
	 * The function will be called after the popup is closed.
	 */
	onOK?: () => void | Promise<void>;
}

export const AlertPopup: FC<IAlertPopup> = ({
	title = 'Something went wrong',
	errorMessage,
	onOK,
	serverError,
	isOpened = false
}: IAlertPopup) => {
	const buttons = [
		{
			title: 'OK',
			onClick: () => {
				Logger.info('Error popup closed on OK click');
				onOK();
			}
		}
	];

	const renderErrorTitle = (): JSX.Element => <div className="error-title">{title}</div>;

	const renderErrorMessage = (): JSX.Element => {
		if (!errorMessage) {
			return null;
		}
		return <div className="error-message">{errorMessage}</div>;
	};

	const renderServerErrorDetails = (): JSX.Element => {
		const serverErrorMessage = getErrorMessage(serverError);
		if (!serverErrorMessage) {
			return null;
		}

		if (serverErrorMessage.includes(EVINCED_SUPPORT_EMAIL)) {
			const splitMessage = serverErrorMessage.split(EVINCED_SUPPORT_EMAIL);
			return (
				<div className="error-details">
					{splitMessage[0] || ''}
					<EvContactSupportEmailLink />
					{splitMessage[1] || ''}
				</div>
			);
		}

		return <div className="error-details">{serverErrorMessage}</div>;
	};

	const isErrorFromClient = isClientError(serverError);

	return (
		<PlatformPopup
			title={title}
			isSrOnly={!isErrorFromClient}
			titleMode={TITLE_MODES.MAIN}
			buttons={buttons}
			isControlled
			isCentered
			isOpen={isOpened}
			className="error-popup"
			onClose={onOK}
			popupWidth="fit-content"
		>
			<div className="alert-popup">
				{!isErrorFromClient && renderErrorTitle()}
				{renderErrorMessage()}
				{renderServerErrorDetails()}
			</div>
		</PlatformPopup>
	);
};
